import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid
} from "recharts";
import {
  Paper,
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  TextField
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid } from "@mui/x-data-grid";
import { getAllEmployeePerformance } from "../State/Employee/Action";
import { FileDownload, PictureAsPdf, Print } from '@mui/icons-material';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from "dayjs";
import { getSelectedExportRows } from "../../config/exportSelection";

const AdminDashboard = () => {
  const dispatch = useDispatch();
  const { employees } = useSelector((store) => store);
  const [open, setOpen] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [modalTitle, setModalTitle] = useState("");
  const [searchText, setSearchText] = useState("");

  const [selectedRowIds, setSelectedRowIds] = useState([]);

  const handleOpen = (orders, title) => {
    setSelectedOrders(orders);
    setModalTitle(title);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  useEffect(() => {
    dispatch(getAllEmployeePerformance());
  }, [dispatch]);

  const columns = [
    {
      field: "serialNo",
      headerName: "S.No.",
      width: 80,
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1
    },
    { field: "name", headerName: "Employee Name", flex: 1 },
    {
      field: "ordersAccepted",
      headerName: "Orders Accepted",
      flex: 1,
      renderCell: (params) => (
        <Typography
          sx={{
            cursor: "pointer",
            color: "blue",
            "&:hover": { color: "darkblue", textDecoration: "underline" }
          }}
          onClick={() =>
            handleOpen(params.row.acceptedOrders, "Orders Accepted")
          }
        >
          {params.value}
        </Typography>
      )
    },
    {
      field: "ordersDeliveredOnTime",
      headerName: "Delivered On Time",
      flex: 1,
      renderCell: (params) => (
        <Typography
          sx={{
            cursor: "pointer",
            color: "blue",
            "&:hover": { color: "darkblue", textDecoration: "underline" }
          }}
          onClick={() =>
            handleOpen(
              params.row.deliveredOnTimeOrders,
              "Orders Delivered On Time"
            )
          }
        >
          {params.value}
        </Typography>
      )
    },
    {
      field: "ordersDeliveredBeforeTime",
      headerName: "Delivered Before Time",
      flex: 1,
      renderCell: (params) => (
        <Typography
          sx={{
            cursor: "pointer",
            color: "blue",
            "&:hover": { color: "darkblue", textDecoration: "underline" }
          }}
          onClick={() =>
            handleOpen(
              params.row.deliveredBeforeTimeOrders,
              "Orders Delivered Before Time"
            )
          }
        >
          {params.value}
        </Typography>
      )
    },
    {
      field: "ordersDeliveredBeyondTime",
      headerName: "Delivered Beyond Time",
      flex: 1,
      renderCell: (params) => (
        <Typography
          sx={{
            cursor: "pointer",
            color: "blue",
            "&:hover": { color: "darkblue", textDecoration: "underline" }
          }}
          onClick={() =>
            handleOpen(
              params.row.deliveredBeyondTimeOrders,
              "Orders Delivered Beyond Time"
            )
          }
        >
          {params.value}
        </Typography>
      )
    },
    {
      field: "ordersWithIssues",
      headerName: "Orders With Issues",
      flex: 1,
      renderCell: (params) => (
        <Typography
          sx={{
            cursor: "pointer",
            color: "blue",
            "&:hover": { color: "darkblue", textDecoration: "underline" }
          }}
          onClick={() =>
            handleOpen(params.row.issuesOrders, "Orders With Issues")
          }
        >
          {params.value}
        </Typography>
      )
    }
  ];

  const rows = employees.employeesPerformance?.map((emp, index) => ({
    id: index,
    name: emp.employee?.name,
    ordersAccepted: emp.ordersAccepted,
    ordersDeliveredOnTime: emp.ordersDeliveredOnTime,
    ordersDeliveredBeforeTime: emp.ordersDeliveredBeforeTime,
    ordersDeliveredBeyondTime: emp.ordersDeliveredBeyondTime,
    ordersWithIssues: emp.ordersWithIssues,
    acceptedOrders: emp.acceptedOrders || [],
    deliveredOnTimeOrders: emp.deliveredOnTimeOrders || [],
    deliveredBeforeTimeOrders: emp.deliveredBeforeTimeOrders || [],
    deliveredBeyondTimeOrders: emp.deliveredBeyondTimeOrders || [],
    issuesOrders: emp.issuesOrders || []
  }));

  const modalColumns = [
    {
      field: "serialNo",
      headerName: "S.No.",
      width: 80,
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1
    },
    { field: "orderId", headerName: "Order ID", flex: 1, minWidth: 80 },
    { field: "orderDate", headerName: "Order Date", flex: 1, minWidth: 100, renderCell: (params) => params.value ? dayjs(params?.value).format("DD MMM YYYY HH:mm") : "----", },
    { field: "productTitle", headerName: "Product Titles", flex: 1, minWidth: 120 },
    { field: "userEmail", headerName: "Customer Email", flex: 1, minWidth: 160 },
    { field: "totalItem", headerName: "Items", flex: 0.7, minWidth: 80 },
    { field: "totalPrice", headerName: "Total Price", flex: 1, minWidth: 100 },
    {
      field: "deliveryDate",
      headerName: "Delivery Date",
      flex: 1,
      minWidth: 100,
      renderCell: (params) => params.value ? dayjs(params?.value).format("DD MMM YYYY") : "----",
    },
    { field: "cancelDate", headerName: "Cancel Date", flex: 1, minWidth: 100, renderCell: (params) => params.value ? dayjs(params?.value).format("DD MMM YYYY") : "----", },
    {
      field: "orderStatus",
      headerName: "Status",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <span
          className={`px-2 py-1 text-xs font-semibold text-white rounded-md ${params.value === "DELIVERED"
            ? "bg-green-500"
            : params.value === "SHIPPED"
              ? "bg-blue-500"
              : params.value === "CANCELLED"
                ? "bg-red-500"
                : params.value === "PLACED"
                  ? "bg-gray-500"
                  : params.value === "PENDING"
                    ? "bg-yellow-500"
                    : "bg-gray-400"
            }`}
        >
          {params.value}
        </span>
      )
    }
  ];

  // Map Data for DataGrid
  const modalRows = selectedOrders?.map((order, index) => ({
    id: order.id || index,
    orderId: order.orderId,
    orderDate: order.orderDate,
    productTitle:
      order.orderItems?.map((item) => item.product?.title).join(", ") || "----",
    userEmail: order.user?.email || "----",
    totalItem: order.totalItem || 0,
    totalPrice: order.totalPrice || 0,
    cancelDate: order.cancelDate,
    deliveryDate: order.deliveryDate,
    orderStatus: order.orderStatus
  }));

  const filteredRows = rows?.filter(row => {
    const query = searchText.toLowerCase();
    return (
      row.name?.toLowerCase().includes(query)
    );
  });

    const getExportData = () => getSelectedExportRows(filteredRows, selectedRowIds);

  const handleExportCSV = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const csvContent = exportRows.map((row, index) => [index + 1,
    row.name, row.ordersAccepted, row.ordersDeliveredOnTime, row.ordersDeliveredBeforeTime, row.ordersDeliveredBeyondTime, row.ordersWithIssues
    ].join(',')).join('\n');
    const headers = ['S.No.', 'Employee Name', 'Orders Accepted', 'Delivered On Time', 'Delivered Before Time', 'Delivered Beyond Time', 'Orders With Issues'].join(',');
    const csv = `${headers}\n${csvContent}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'employee_performance.csv';
    link.click();
  };
  const handleExportPDF = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const doc = new jsPDF();
    doc.setFontSize(12);
    doc.text("Employee Performance", 14, 15);
    const tableColumn = ['S.No.', "Employee Name", "Orders Accepted", "Delivered On Time", "Delivered Before Time", "Delivered Beyond Time", "Orders With Issues"];
    const tableRows = exportRows.map((row, index) => [index + 1, row.name, row.ordersAccepted, row.ordersDeliveredOnTime, row.ordersDeliveredBeforeTime, row.ordersDeliveredBeyondTime, row.ordersWithIssues]);
    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
      theme: 'striped',
      headStyles: {
        fillColor: [37, 99, 235], // Tailwind blue-600 (RGB: 37,99,235)
        textColor: [255, 255, 255], // white text
        halign: 'left',
      },
      styles: { fontSize: 8, cellPadding: 1, overflow: 'linebreak' },
    });
    doc.save('employee_performance.pdf');
  };
  const handlePrint = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const printWindow = window.open('', '_blank');
    const headers = ['S.No.', 'Employee Name', 'Orders Accepted', 'Delivered On Time', 'Delivered Before Time', 'Delivered Beyond Time', 'Orders With Issues'];
    const tableContent = exportRows.map((row, index) => [index + 1, row.name, row.ordersAccepted, row.ordersDeliveredOnTime, row.ordersDeliveredBeforeTime, row.ordersDeliveredBeyondTime, row.ordersWithIssues]);
    const html = `
      <html>
        <head>
          <title>Employee Performance</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid black; padding: 8px; text-align: left; }
            th { background: #449AEF;
                color: white;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact; }
            tr:nth-child(even) { background-color: #f5f5f5; }
          </style>
        </head>
        <body>
          <h2>Employee Performance</h2>
          <table>
            <thead>
              <tr>
                ${headers.map(header => `<th>${header}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${tableContent.map(row => `
                <tr>
                  ${row.map(cell => `<td>${cell}</td>`).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div>
      <Paper elevation={3} sx={{ p: 2, borderRadius: 2, boxShadow: 1 }}>
        <h2 className="text-xl font-bold text-center text-uppercase ">
          EMPLOYEE PERFORMANCER DASHBOARD
        </h2>

        {/* Graph */}
        <ResponsiveContainer width="100%" height={350}>
          <BarChart
            data={employees?.employeesPerformance}
            margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
            barCategoryGap="15%"
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />

            <XAxis
              dataKey="employee.name"
              angle={-35}
              textAnchor="end"
              interval={0}
              height={80}
              tick={{ fontSize: 12 }}
            />

            <YAxis
              tick={{ fontSize: 12 }}
              allowDecimals={false}
            />

            <Tooltip
              wrapperStyle={{ fontSize: "14px" }}
              contentStyle={{ backgroundColor: "#fff", borderRadius: "8px", border: "1px solid #ccc" }}
              cursor={{ fill: "#f0f0f0" }}
            />

            <Legend verticalAlign="top" height={36} iconSize={12} />

            <Bar
              dataKey="ordersAccepted"
              fill="#FFB347"
              name="Accepted"
              radius={[6, 6, 0, 0]}
            />
            <Bar
              dataKey="ordersDeliveredOnTime"
              fill="#4CAF50"
              name="Delivered On Time"
              radius={[6, 6, 0, 0]}
            />
            <Bar
              dataKey="ordersDeliveredBeforeTime"
              fill="#2196F3"
              name="Delivered Before Time"
              radius={[6, 6, 0, 0]}
            />
            <Bar
              dataKey="ordersDeliveredBeyondTime"
              fill="#FF9800"
              name="Delivered Late"
              radius={[6, 6, 0, 0]}
            />
            <Bar
              dataKey="ordersWithIssues"
              fill="#F44336"
              name="With Issues"
              radius={[6, 6, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>

        {/* Table */}
        <Paper elevation={3} sx={{ p: 2, borderRadius: 2, boxShadow: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              EMPLOYEE PERFORMANCE
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                variant="contained"
                startIcon={<FileDownload />}
                onClick={handleExportCSV}
                sx={{
                  background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  borderRadius: '6px',
                  px: 2,
                  py: 0.8,
                  transition: 'all 0.2s',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                    boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                    transform: 'scale(1.02)',
                  },
                  cursor: 'pointer',
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Export CSV
              </Button>
              <Button
                variant="contained"
                startIcon={<PictureAsPdf />}
                onClick={handleExportPDF}
                sx={{
                  background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  borderRadius: '6px',
                  px: 2,
                  py: 0.8,
                  transition: 'all 0.2s',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                    boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                    transform: 'scale(1.02)',
                  },
                  cursor: 'pointer',
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Export PDF
              </Button>
              <Button
                variant="contained"
                startIcon={<Print />}
                onClick={handlePrint}
                sx={{
                  background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  borderRadius: '6px',
                  px: 2,
                  py: 0.8,
                  transition: 'all 0.2s',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                    boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                    transform: 'scale(1.02)',
                  },
                  cursor: 'pointer',
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Print
              </Button>
              <TextField
                label="Search Employee"
                variant="outlined"
                size="small"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                sx={{ width: { xs: '100%', sm: '220px' } }}
              />
            </Box>
          </Box>
          <Box sx={{ height: "100%", width: "100%" }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              pagination
              initialState={{
                pagination: { paginationModel: { pageSize: 10, page: 0 } },
              }}
              pageSizeOptions={[10, 25, 50, 75, 100]}
              getRowId={row => row.id}
                checkboxSelection
              onRowSelectionModelChange={(ids) => setSelectedRowIds(ids)}
              rowSelectionModel={selectedRowIds}
              disableSelectionOnClick
              density="compact"
              experimentalFeatures={{ virtualization: true }}
              loading={employees.loading}
              sx={{
                borderRadius: 2,
                fontSize: "0.8rem",
                "& .MuiDataGrid-cell": {
                  fontSize: "0.8rem",
                  border: "1px solid rgb(204, 201, 201)",
                  minHeight: "36px !important",
                  height: "36px !important",
                  lineHeight: "1.2",
                  paddingTop: "4px",
                  paddingBottom: "4px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "#449AEF",
                  color: "#fff",
                  fontWeight: "bold",
                  fontSize: "0.8rem",
                  padding: "8px",
                  border: "1px solid rgb(204, 201, 201)",
                  minHeight: "36px !important",
                  height: "36px !important",
                  lineHeight: "1.2",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontSize: "0.8rem",
                  fontWeight: 600,
                },
                "& .MuiDataGrid-row": {
                  maxHeight: "36px !important",
                  minHeight: "36px !important",
                },
                "& .MuiDataGrid-row:nth-of-type(odd)": {
                  backgroundColor: "#f5f5f5",
                },
                "& .MuiDataGrid-row:nth-of-type(even)": {
                  backgroundColor: "#ffffff",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
          </Box>
        </Paper>

      </Paper>
      {/* Modal for Orders List */}
      <Modal open={open} onClose={handleClose}>
        <Box className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-4 rounded-lg shadow-lg w-[90vw] h-[80vh] max-w-4xl overflow-auto">
          {/* Modal Header */}
          <div className="flex justify-between items-center  pb-3">
            <Typography variant="h6" component="h2">
              {modalTitle.toUpperCase()}
            </Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </div>

          {/* Responsive DataGrid Table */}
          <Paper
            sx={{
              width: "100%",
              marginTop: 1,
              overflowX: "auto"
            }}
          >
            <DataGrid
              rows={modalRows}
              columns={modalColumns}
              pagination
              initialState={{
                pagination: { paginationModel: { pageSize: 10, page: 0 } },
              }}
              pageSizeOptions={[10, 25, 50, 75, 100]}
              getRowId={(row) => row.id}
              disableSelectionOnClick
              density="compact"
              experimentalFeatures={{ virtualization: true }}
              loading={employees.loading}
              sx={{
                borderRadius: 2,
                fontSize: "0.8rem",
                "& .MuiDataGrid-cell": {
                  fontSize: "0.8rem",
                  border: "1px solid rgb(204, 201, 201)",
                  minHeight: "36px !important",
                  height: "36px !important",
                  lineHeight: "1.2",
                  paddingTop: "4px",
                  paddingBottom: "4px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "#449AEF",
                  color: "#fff",
                  fontWeight: "bold",
                  fontSize: "0.8rem",
                  padding: "8px",
                  border: "1px solid rgb(204, 201, 201)",
                  minHeight: "36px !important",
                  height: "36px !important",
                  lineHeight: "1.2",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontSize: "0.8rem",
                  fontWeight: 600,
                },
                "& .MuiDataGrid-row": {
                  maxHeight: "36px !important",
                  minHeight: "36px !important",
                },
                "& .MuiDataGrid-row:nth-of-type(odd)": {
                  backgroundColor: "#f5f5f5",
                },
                "& .MuiDataGrid-row:nth-of-type(even)": {
                  backgroundColor: "#ffffff",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "#f5f5f5",
                },
                height: 400,
              }}
            />
          </Paper>
        </Box>
      </Modal>
    </div>
  );
};

export default AdminDashboard;
